import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarClock, Users, ArrowRight, Flame } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/landing/Reveal";
import { useEligibility } from "@/components/landing/EligibilityContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pad = (n) => String(n).padStart(2, "0");

function useCountdown(targetIso) {
  const compute = useCallback(() => {
    if (!targetIso) return null;
    const target = new Date(`${targetIso}T09:00:00`).getTime();
    const diff = target - Date.now();
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, over: true };
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff / 3600000) % 24),
      m: Math.floor((diff / 60000) % 60),
      s: Math.floor((diff / 1000) % 60),
      over: false,
    };
  }, [targetIso]);

  const [t, setT] = useState(compute);
  useEffect(() => {
    setT(compute());
    const id = setInterval(() => setT(compute()), 1000);
    return () => clearInterval(id);
  }, [compute]);
  return t;
}

const TimeCell = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="flex min-w-[3.5rem] items-center justify-center rounded-xl border border-surfaceline bg-ink px-3 py-2.5 font-display text-3xl font-bold text-em2 sm:min-w-[4.5rem] sm:text-4xl">
      {pad(value)}
    </div>
    <span className="mt-1.5 text-[0.62rem] font-bold uppercase tracking-[0.18em] text-mutedgreen">
      {label}
    </span>
  </div>
);

export const BatchCountdown = () => {
  const { open } = useEligibility();
  const [batch, setBatch] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/batches`)
      .then(({ data }) => {
        const today = new Date().toISOString().slice(0, 10);
        const upcoming = data
          .filter((b) => b.start_date >= today)
          .sort((a, z) => a.start_date.localeCompare(z.start_date));
        setBatch(upcoming[0] ?? null);
      })
      .catch(() => setBatch(null))
      .finally(() => setLoaded(true));
  }, []);

  const t = useCountdown(batch?.start_date);
  if (!loaded || !batch) return null;

  const total = batch.total_seats || 0;
  const left = Math.max(0, batch.seats_left ?? 0);
  const filledPct = total > 0 ? Math.min(100, ((total - left) / total) * 100) : 0;
  const dateLabel = new Date(`${batch.start_date}T09:00:00`).toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <section className="relative px-4 py-16 sm:px-8 lg:py-24" data-testid="batch-countdown-section">
      <div className="mx-auto max-w-5xl">
        <Reveal className="mb-8 text-center">
          <SectionLabel className="justify-center">Upcoming Ahmedabad Batch</SectionLabel>
          <h2 className="mt-4 font-display text-5xl font-bold uppercase leading-none tracking-tight text-offwhite sm:text-6xl">
            Seats Are Filling Fast
          </h2>
        </Reveal>

        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-em2/35 bg-surface p-7 glow-emerald sm:p-10">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-em1/15 blur-[70px]" />
            <div className="relative z-10 grid gap-8 lg:grid-cols-2 lg:items-center">
              <div>
                <span
                  className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-ink px-3 py-1 text-xs font-bold uppercase tracking-wider text-gold"
                  data-testid="batch-name"
                >
                  <CalendarClock size={14} /> {batch.name}
                </span>
                <p className="mt-4 text-lg font-semibold text-offwhite">Starts {dateLabel}</p>
                <div className="mt-5 flex items-end gap-2 sm:gap-3" data-testid="countdown-timer">
                  {t?.over ? (
                    <p className="font-display text-3xl font-bold uppercase text-em2">
                      Enrolling now — last seats!
                    </p>
                  ) : (
                    <>
                      <TimeCell value={t?.d ?? 0} label="Days" />
                      <span className="pb-4 font-display text-3xl font-bold text-mutedgreen/50">:</span>
                      <TimeCell value={t?.h ?? 0} label="Hrs" />
                      <span className="pb-4 font-display text-3xl font-bold text-mutedgreen/50">:</span>
                      <TimeCell value={t?.m ?? 0} label="Min" />
                      <span className="pb-4 font-display text-3xl font-bold text-mutedgreen/50">:</span>
                      <TimeCell value={t?.s ?? 0} label="Sec" />
                    </>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-surfaceline bg-ink/60 p-6">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-semibold text-offwhite">
                    <Users size={16} className="text-em2" /> Seats remaining
                  </span>
                  <span
                    className="flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-sm font-bold text-red-400"
                    data-testid="seats-left"
                  >
                    <Flame size={14} /> Only {left} left
                  </span>
                </div>
                <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-ink">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-em1 to-em2"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${filledPct}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
                </div>
                <p className="mt-2 text-xs text-mutedgreen">
                  {total - left} of {total} desks already booked
                </p>
                <button
                  onClick={open}
                  data-testid="batch-inquire-btn"
                  className="btn-emerald shimmer-cta mt-5 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full font-bold text-white transition-transform hover:scale-[1.02]"
                >
                  Reserve My Seat <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
